/**
 * HomeView — the landing screen: a centered greeting over the new-chat
 * composer (Gemini-style).
 *
 *   ┌─────────────────────────────────────────────────────┐
 *   │                                                     │
 *   │            Good morning, Shouvik                    │
 *   │                                                     │
 *   │     [ chat input — centered ]                       │
 *   │                                                     │
 *   └─────────────────────────────────────────────────────┘
 *
 * The Phase-1 "Today" dashboard widget that used to render here was
 * removed (2026-08) pending a better approach — see the v44 DB
 * migration. When a dashboard returns, this is where it mounts.
 */

import { useEffect, useMemo } from "react";

import { useAuthStore } from "@/stores/authStore";
import { useChatStore } from "@/stores/chatStore";
import { HomeChatInput } from "./HomeChatInput";

// ─── Greeting helpers ─────────────────────────────────────────────────

function getTimeGreeting(): string {
  const hr = new Date().getHours();
  if (hr < 5) return "Working late";
  if (hr < 12) return "Good morning";
  if (hr < 17) return "Good afternoon";
  if (hr < 22) return "Good evening";
  return "Working late";
}

function firstNameOf(name: string | undefined): string {
  if (!name) return "there";
  return name.trim().split(/\s+/)[0] || "there";
}

export function HomeView() {
  const userName = useAuthStore((s) => s.user?.name);
  const resetActiveContext = useChatStore((s) => s.resetActiveContext);
  const setHomeDraft = useChatStore((s) => s.setHomeDraft);

  // Landing on home means starting fresh — there is no active conversation.
  // Clear the per-conversation context/usage state so stale numbers from the
  // chat the user just left (most visibly the model-switch lock, which keys
  // off ``contextTokens``) don't carry over to the new-conversation input.
  //
  // Also clear the composer draft so each *fresh* home visit starts empty —
  // but never wipe a deep-link seed waiting to be dropped in (see below).
  useEffect(() => {
    resetActiveContext();
    if (!useChatStore.getState().pendingHomeSeed) setHomeDraft("");
  }, [resetActiveContext, setHomeDraft]);

  // Drop an omniwerk:// deep-link seed into the composer when one arrives —
  // covers both a fresh mount and HomeView already being open. Consume it so a
  // later genuine home visit starts empty.
  const pendingHomeSeed = useChatStore((s) => s.pendingHomeSeed);
  const setPendingHomeSeed = useChatStore((s) => s.setPendingHomeSeed);
  useEffect(() => {
    if (!pendingHomeSeed) return;
    setHomeDraft(pendingHomeSeed);
    setPendingHomeSeed(null);
  }, [pendingHomeSeed, setHomeDraft, setPendingHomeSeed]);

  // Pre-compute the greeting line per render (cheap; tiny string ops)
  // so the value lands even before the auth store hydrates.
  const greeting = useMemo(
    () => `${getTimeGreeting()}, ${firstNameOf(userName)}`,
    [userName],
  );

  return (
    <div className="h-full overflow-y-auto bg-space-20">
      <div className="h-full flex flex-col max-w-7xl mx-auto px-6 sm:px-10 pt-10 pb-6">
        {/* Vertically and horizontally centered greeting + composer. */}
        <section className="flex-1 flex flex-col items-center justify-center w-full pb-10">
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-space mb-8
                         animate-[onbFadeIn_500ms_ease-out_both] text-center">
            {greeting}
          </h1>
          <HomeChatInput />
        </section>
      </div>
    </div>
  );
}
